import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSuggestions } from '../lib/useSuggestions';

export default function Suggestions() {
  const navigate = useNavigate();
  const { loading, error, data } = useSuggestions();

  const goToNewRequest = (modulId, tipId) => {
    if (modulId && tipId) {
      navigate(`/new-request?modul=${modulId}&tip=${tipId}`);
    } else {
      navigate('/new-request');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Sugestii pentru tine
        </Typography>
      </Box>

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Nu am putut încărca sugestiile. Încearcă din nou mai târziu.
        </Alert>
      )}

      {data && !error && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
          {/* Recomandarea de profil */}
          <Box sx={{ flex: { xs: '0 0 100%', md: '0 0 calc(50% - 12px)' } }}>
            <Card sx={{ height: '100%', borderRadius: 3, minHeight: { xs: 260, md: 280 } }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SchoolIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Profilul tău recomandat
                  </Typography>
                </Box>
                {data.profileRec ? (
                  <>
                    <Chip
                      label={data.profileRec.profil}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 'bold', mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {data.profileRec.descriere}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, mt: 'auto' }}>
                      Direcții potrivite
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {data.profileRec.domenii.map((d) => (
                        <Chip key={d} label={d} size="small" />
                      ))}
                    </Box>
                  </>
                ) : (
                  <Alert severity="info" icon={<InfoIcon fontSize="inherit" />}>
                    Completează specializarea în pagina de profil pentru a primi recomandări
                    personalizate.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Sugestii de carieră */}
          <Box sx={{ flex: { xs: '0 0 100%', md: '0 0 calc(50% - 12px)' } }}>
            <Card sx={{ height: '100%', borderRadius: 3, minHeight: { xs: 260, md: 280 } }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUpIcon color="secondary" />
                  <Typography variant="h6" fontWeight="bold">
                    Sugestii de carieră
                  </Typography>
                </Box>
                {data.careerSuggestions.length > 0 ? (
                  <Box>
                    {data.careerSuggestions.map((c, index) => (
                      <Box key={c.domeniu} sx={{ mb: 1.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {c.domeniu}
                        </Typography>
                        {c.surse.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {c.surse.map((s) => (
                              <Chip key={s} label={`Bazat pe ${s}`} size="small" variant="outlined" />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Bazat pe profilul tău
                          </Typography>
                        )}
                        {index < data.careerSuggestions.length - 1 && (
                          <Divider sx={{ mt: 1.5 }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Acumulează credite în module pentru a primi sugestii de carieră bazate pe
                    activitatea ta.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Module unde mai are nevoie de credite */}
          <Box sx={{ flex: '0 0 100%' }}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MenuBookIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Discipline unde mai ai nevoie de credite
                  </Typography>
                </Box>

                {!data.hasRequests && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Depune prima cerere de credit ca să primești sugestii personalizate.
                  </Alert>
                )}

                {data.moduleGaps.length === 0 && data.targetModules > 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Ai atins ținta la toate modulele configurate. Felicitări!
                  </Alert>
                )}

                {data.moduleGaps.length === 0 && data.targetModules === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nu există module cu țintă de credite configurată.
                  </Typography>
                )}

                {data.moduleGaps.slice(0, 5).map((gap, index) => (
                  <Box key={gap.id} sx={{ mb: 2.5, '&:last-child': { mb: 0 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Chip label={gap.cod} size="small" color="primary" variant="outlined" sx={{ flexShrink: 0 }} />
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                          {gap.nume}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ ml: 1, flexShrink: 0 }}>
                        {gap.total} / {gap.tinta}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={gap.progres}
                      color={gap.total >= gap.tinta ? 'success' : 'primary'}
                      sx={{ height: 6, borderRadius: 3, mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Mai ai nevoie de <strong>{gap.gap} credite</strong> pentru a atinge ținta
                      modulului.
                    </Typography>
                    {gap.activitati.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Activități sugerate:
                        </Typography>
                        {gap.activitati.map((a) => (
                          <Chip key={a.id} label={a.nume} size="small" variant="outlined" sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                        ))}
                        <Button
                          size="small"
                          variant="contained"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => goToNewRequest(gap.id, gap.activitati[0].id_tip_credit)}
                          sx={{
                            ml: { xs: 0, sm: 'auto' },
                            mt: { xs: 1, sm: 0 },
                            width: { xs: '100%', sm: 'auto' },
                            textTransform: 'none',
                          }}
                        >
                          Depune cerere
                        </Button>
                      </Box>
                    )}
                    {gap.activitati.length === 0 && (
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => goToNewRequest(gap.id, null)}
                        sx={{ textTransform: 'none' }}
                      >
                        Depune o cerere nouă
                      </Button>
                    )}
                    {index < data.moduleGaps.slice(0, 5).length - 1 && <Divider sx={{ mt: 2.5 }} />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Container>
  );
}
